import styles from "./LotteryRoulette.module.scss"

// 止まった瞬間に盤面から弾ける紙吹雪の枚数.
const CONFETTI_COUNT = 40

// 弾けた後に、上から降ってくる紙吹雪の枚数.
const SHOWER_COUNT = 36

// 降ってくる紙吹雪が落ちきる距離 (px). 結果表示のモーダルの高さ (約 700px) より少し長くして、下まで落とす.
const SHOWER_FALL_DISTANCE = 780

// 紙吹雪の色. 色の値そのものは scss に置き (ルーレットの配色と同じ変数を使うため)、ここでは順番だけ決める.
const CONFETTI_COLOR_CLASS_NAMES = [
	styles.confettiPink,
	styles.confettiGold,
	styles.confettiPinkSoft,
	styles.confettiGoldPale,
	styles.confettiPinkDeep,
]

// burst = 盤面の中心から弾ける / shower = 上から降ってくる.
type ConfettiKind = "burst" | "shower"

export type ConfettiPiece = {
	id: number
	kind: ConfettiKind
	// shower の時だけ使う. 降り始める横位置 (面の左端からの割合, %). burst は常に中央 (50).
	xPercent: number
	// 盤面の中心からの最終的な横方向の移動量 (px)
	dx: number
	// いったん跳ね上がる高さ (px, 上方向なので負の値)
	rise: number
	// 最後に落ちていく距離 (px)
	fall: number
	rotate: number
	delayMs: number
	durationMs: number
	width: number
	height: number
	colorClassName: string
}

// scss の @keyframes lottery-confetti が読む CSS 変数.
type ConfettiPieceStyle = React.CSSProperties & {
	"--dx": string
	"--rise": string
	"--fall": string
	"--rotate": string
}

const randomBetween = (min: number, max: number): number =>
	min + Math.random() * (max - min)

export const newConfettiPieces = (): ConfettiPiece[] => {
	const pieces: ConfettiPiece[] = []
	for (let i = 0; i < CONFETTI_COUNT; i++) {
		pieces.push({
			id: i,
			kind: "burst",
			xPercent: 50,
			dx: randomBetween(-170, 170),
			rise: randomBetween(-210, -70),
			fall: randomBetween(180, 340),
			rotate: randomBetween(-720, 720),
			delayMs: randomBetween(0, 180),
			durationMs: randomBetween(1500, 2400),
			width: randomBetween(6, 10),
			height: randomBetween(8, 14),
			colorClassName:
				CONFETTI_COLOR_CLASS_NAMES[i % CONFETTI_COLOR_CLASS_NAMES.length],
		})
	}
	for (let i = 0; i < SHOWER_COUNT; i++) {
		pieces.push({
			id: CONFETTI_COUNT + i,
			kind: "shower",
			xPercent: randomBetween(2, 98),
			// 降りながら左右に流れる量
			dx: randomBetween(-50, 50),
			rise: 0,
			fall: SHOWER_FALL_DISTANCE,
			rotate: randomBetween(-900, 900),
			// 弾けた紙吹雪が落ち始める頃から、ばらばらに降らせる
			delayMs: randomBetween(350, 1900),
			durationMs: randomBetween(2200, 3400),
			width: randomBetween(6, 10),
			height: randomBetween(8, 14),
			colorClassName:
				CONFETTI_COLOR_CLASS_NAMES[i % CONFETTI_COLOR_CLASS_NAMES.length],
		})
	}
	return pieces
}

const confettiPieceStyle = (
	piece: ConfettiPiece,
	originY: number
): ConfettiPieceStyle => {
	// shower は面の上端の少し上から降らせる. burst は盤面の中心から.
	let top = originY
	if (piece.kind === "shower") {
		top = -20
	}
	return {
		"--dx": `${piece.dx}px`,
		"--rise": `${piece.rise}px`,
		"--fall": `${piece.fall}px`,
		"--rotate": `${piece.rotate}deg`,
		top: `${top}px`,
		left: `${piece.xPercent}%`,
		width: `${piece.width}px`,
		height: `${piece.height}px`,
		animationDelay: `${piece.delayMs}ms`,
		animationDuration: `${piece.durationMs}ms`,
	}
}

const confettiPieceClassName = (piece: ConfettiPiece): string => {
	if (piece.kind === "shower") {
		return `${styles.confettiPiece} ${styles.confettiShower} ${piece.colorClassName}`
	}
	return `${styles.confettiPiece} ${piece.colorClassName}`
}

type Props = {
	pieces: ConfettiPiece[]
	// 紙吹雪が弾ける起点 (盤面の中心) の、ルーレットの面の上端からの距離 (px).
	originY: number
}

// 止まった瞬間に盤面から弾け、続けて上から降ってくる紙吹雪. 見た目だけの装飾なので読み上げない.
const LotteryConfetti = ({ pieces, originY }: Props) => {
	return (
		<div className={styles.confetti} aria-hidden="true">
			{pieces.map((piece) => (
				<span
					key={piece.id}
					className={confettiPieceClassName(piece)}
					style={confettiPieceStyle(piece, originY)}
				/>
			))}
		</div>
	)
}

export default LotteryConfetti
